import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, Sparkles, CheckCircle2, Star, Save, Trash2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Button, Input, Textarea, Disclaimer, useToast } from "@/components/earnos/kit";
import { Orb } from "@/components/earnos/Orb";

export default function OfferBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const preparedOffer = location.state?.preparedOffer;
  const toast = useToast();
  const [form, setForm] = useState({ selling: "", audience: preparedOffer?.target_customer || "", problem: "", included: "", delivery_time: "", price: "", value: "" });
  const mutationLock = useRef(false);
  const [saving, setSaving] = useState(false);
  const [offersError, setOffersError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(preparedOffer ? { ...preparedOffer, benefits: [] } : null);
  const [offers, setOffers] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setOffersError("");
    try { const d = await apiFetch("/offers"); setOffers(d.offers); } catch (e) { setOffersError(e.message || "Saved offers unavailable."); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!form.selling.trim()) { toast.show("Tell EARNOS what you're selling", "error"); return; }
    if (mutationLock.current) return;
    mutationLock.current = true; setGenerating(true);
    try {
      const r = await apiFetch("/offers/generate", { method: "POST", body: form });
      setResult({
        name: r.name || "", short_description: r.short_description || "", detailed_description: r.detailed_description || "",
        deliverables: r.deliverables || [], price: r.suggested_price || parseFloat(form.price) || 0,
        benefits: r.customer_benefits || [], faqs: r.faqs || [], cta: r.cta || "",
      });
    } catch (e) {
      toast.show(e?.message || "Could not generate offer", "error");
    } finally {
      mutationLock.current = false; setGenerating(false);
    }
  };

  const saveOffer = async () => {
    if (!result || mutationLock.current) return;
    mutationLock.current = true; setSaving(true);
    try {
      await apiFetch("/offers", {
        method: "POST",
        body: {
          name: result.name, short_description: result.short_description, detailed_description: result.detailed_description,
          deliverables: result.deliverables, price: result.price, target_customer: form.audience, faqs: result.faqs, cta: result.cta,
        },
      });
      toast.show("Offer saved", "success");
      setResult(null);
      setForm({ selling: "", audience: "", problem: "", included: "", delivery_time: "", price: "", value: "" });
      load();
    } catch (e) {
      toast.show(e?.message || "Failed to save", "error");
    } finally { mutationLock.current = false; setSaving(false); }
  };

  const deleteOffer = async (offer_id) => {
    if (mutationLock.current) return;
    mutationLock.current = true;
    try { await apiFetch(`/offers/${offer_id}`, { method: "DELETE" }); toast.show("Offer deleted", "info"); await load(); }
    catch (e) { toast.show(e.message || "Could not delete offer", "error"); }
    finally { mutationLock.current = false; }
  };

  return (
    <div className="animate-fade-in max-w-2xl">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} data-testid="offer-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">Offer Builder</h1>
      </div>

      {offersError && <p role="alert" className="text-warn my-4">{offersError} <button type="button" onClick={load} className="underline">Retry saved offers</button></p>}
      {!result ? (
        <>
          <div className="flex items-center gap-3 mb-6">
            <Orb state="idle" size={56} />
            <p className="text-base text-ink-2 leading-relaxed">Answer a few questions and EARNOS will package a polished, sellable offer.</p>
          </div>
          <Input label="What are you selling?" value={form.selling} onChange={(e) => set("selling", e.target.value)} placeholder="e.g. Short-form video editing" testID="offer-selling" />
          <Input label="Who is it for?" value={form.audience} onChange={(e) => set("audience", e.target.value)} placeholder="e.g. Local restaurants" />
          <Textarea label="What problem does it solve?" value={form.problem} onChange={(e) => set("problem", e.target.value)} placeholder="e.g. They have no time for content" rows={2} />
          <Textarea label="What's included?" value={form.included} onChange={(e) => set("included", e.target.value)} placeholder="e.g. 12 videos, captions, music" rows={2} />
          <Input label="Delivery time" value={form.delivery_time} onChange={(e) => set("delivery_time", e.target.value)} placeholder="e.g. 5 business days" />
          <Input label="Starting price ($)" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="e.g. 300" />
          <Textarea label="What makes it valuable?" value={form.value} onChange={(e) => set("value", e.target.value)} placeholder="e.g. Consistent, on-brand content" rows={2} />
          <Button title="Generate My Offer" icon={Sparkles} onClick={generate} loading={generating} full testID="offer-generate" />

          {offers.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-bold text-ink mb-3">Saved offers</h3>
              <div className="space-y-3">
                {offers.map((o) => (
                  <div key={o.offer_id} className="flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-lg font-bold text-ink">{o.name}</p>
                      <p className="text-sm text-ink-2 line-clamp-2">{o.short_description}</p>
                      {o.price ? <p className="text-base font-bold text-brand-light mt-1">${o.price}</p> : null}
                    </div>
                    <button aria-label={`Delete ${o.name}`} onClick={() => deleteOffer(o.offer_id)} className="text-danger"><Trash2 size={20} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="earnos-kicker mb-4">AI-generated · edit anything before saving</p>
          <Input label="Target customer" value={form.audience} onChange={(e) => set("audience", e.target.value)} />
          <Input label="Offer name" value={result.name} onChange={(e) => setResult({ ...result, name: e.target.value })} testID="result-name" />
          <Textarea label="Short description" value={result.short_description} onChange={(e) => setResult({ ...result, short_description: e.target.value })} rows={2} />
          <Textarea label="Detailed description" value={result.detailed_description} onChange={(e) => setResult({ ...result, detailed_description: e.target.value })} rows={4} />
          <Input label="Suggested price ($)" type="number" value={result.price} onChange={(e) => setResult({ ...result, price: parseFloat(e.target.value) || 0 })} />

          <h3 className="text-lg font-bold text-ink mt-4 mb-2">Deliverables</h3>
          {result.deliverables.map((d, i) => <div key={i} className="flex items-start gap-2 mb-2"><CheckCircle2 size={16} className="text-ok mt-0.5 shrink-0" /><span className="text-base text-ink-3 leading-relaxed">{d}</span></div>)}
          <h3 className="text-lg font-bold text-ink mt-4 mb-2">Customer benefits</h3>
          {result.benefits.map((d, i) => <div key={i} className="flex items-start gap-2 mb-2"><Star size={14} className="text-brand-light mt-1 shrink-0" /><span className="text-base text-ink-3 leading-relaxed">{d}</span></div>)}
          {result.faqs?.length > 0 && (
            <>
              <h3 className="text-lg font-bold text-ink mt-4 mb-2">FAQs</h3>
              {result.faqs.map((f, i) => (
                <div key={i} className="rounded-xl border border-line bg-surface-2 p-3 mb-2">
                  <p className="text-base font-bold text-ink">{f.q}</p>
                  <p className="text-base text-ink-2 mt-0.5 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </>
          )}
          {result.cta ? <div className="rounded-xl bg-brand/10 border border-brand/30 p-4 mt-3"><p className="text-lg font-bold text-ink text-center">{result.cta}</p></div> : null}

          <Button title="Save Offer" loading={saving} icon={Save} onClick={saveOffer} full className="mt-6" testID="offer-save" />
          <Button title="Start Over" variant="ghost" onClick={() => setResult(null)} full className="mt-2" />
          <Disclaimer text="Suggested pricing is an estimate. You set your final price." />
        </>
      )}
    </div>
  );
}
